<template>
  <div class="space-overview">
    <div class="space-header">
      <div class="space-info">
        <h2>{{ currentSpace.name }}</h2>
        <p class="space-id">SpaceID: {{ currentSpace.id }}</p>
        <div class="api-key-row" v-if="currentSpace.api_key">
          <span class="api-key-label">API Key:</span>
          <el-input 
            :value="currentSpace.api_key" 
            readonly 
            size="small"
            class="api-key-input"
          >
            <el-button 
              slot="append" 
              icon="el-icon-document-copy" 
              @click="copyApiKey"
              title="复制 API Key"
            ></el-button>
          </el-input>
        </div>
      </div>
    </div>

    <div class="service-grid">
      <el-card 
        v-for="service in services" 
        :key="service.key"
        class="service-card"
        shadow="hover"
        @click.native="navigateTo(service.path)"
      >
        <div class="service-icon" :style="{ background: service.color }">
          <i :class="service.icon"></i>
        </div>
        <h3>{{ service.title }}</h3>
        <p>{{ service.description }}</p>
        <div class="service-stats" v-if="stats[service.key]">
          <span>{{ stats[service.key] }} 个</span>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpaceOverview',
  data() {
    return {
      currentSpace: {},
      stats: {
        databases: 0,
        functions: 0,
        buckets: 0
      },
      services: [
        {
          key: 'databases',
          title: '云数据库',
          description: '提供 NoSQL 数据库服务',
          icon: 'el-icon-coin',
          color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          path: 'databases'
        },
        {
          key: 'functions',
          title: '云函数',
          description: '无需服务器的函数计算服务',
          icon: 'el-icon-s-promotion',
          color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          path: 'functions'
        },
        {
          key: 'buckets',
          title: '云存储',
          description: '文件存储和CDN加速服务',
          icon: 'el-icon-folder-opened',
          color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          path: 'storage'
        },
        {
          key: 'sites',
          title: '前端网页托管',
          description: '静态网站托管服务',
          icon: 'el-icon-monitor',
          color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          path: 'sites'
        },
        {
          key: 'logs',
          title: 'API 日志',
          description: '查看和管理 API 调用日志',
          icon: 'el-icon-document',
          color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          path: 'logs'
        }
      ]
    };
  },
  created() {
    this.loadSpaceInfo();
    this.loadStats();
  },
  methods: {
    loadSpaceInfo() {
      const spaceId = this.$route.params.id;
      const space = this.$store.state.currentProject;
      
      if (space && space.id == spaceId) {
        this.currentSpace = space;
      } else {
        // 从 API 获取
        this.fetchSpaceInfo(spaceId);
      }
    },
    async fetchSpaceInfo(spaceId) {
      try {
        const response = await this.$axios.get(`/projects/${spaceId}`);
        this.currentSpace = response.data;
        this.$store.commit('SET_CURRENT_PROJECT', response.data);
      } catch (error) {
        this.$message.error('获取服务空间信息失败');
        this.$router.push('/projects');
      }
    },
    async loadStats() {
      const spaceId = this.$route.params.id;
      
      try {
        // 获取数据库数量
        const dbResponse = await this.$axios.get(`/projects/${spaceId}/databases`);
        this.stats.databases = dbResponse.data.length;
        
        // 获取云函数数量
        const funcResponse = await this.$axios.get(`/projects/${spaceId}/functions`);
        this.stats.functions = funcResponse.data.length;
        
        // 获取存储桶数量
        const bucketResponse = await this.$axios.get(`/projects/${spaceId}/buckets`);
        this.stats.buckets = bucketResponse.data.length;
      } catch (error) {
        console.error('获取统计信息失败:', error);
      }
    },
    navigateTo(path) {
      const spaceId = this.$route.params.id;
      this.$router.push(`/space/${spaceId}/${path}`);
    },
    
    async copyApiKey() {
      const apiKey = this.currentSpace.api_key;
      
      if (!apiKey) {
        this.$message.warning('API Key 不存在');
        return;
      }
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(apiKey);
          this.$message.success('API Key 已复制到剪贴板');
        } catch (err) {
          this.fallbackCopy(apiKey);
        }
      } else {
        this.fallbackCopy(apiKey);
      }
    },
    
    fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        this.$message.success('API Key 已复制到剪贴板');
      } catch (err) {
        this.$message.error('复制失败，请手动复制');
      }
      
      document.body.removeChild(textarea);
    }
  }
};
</script>

<style scoped>
.space-overview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.space-header {
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.space-info h2 {
  font-size: 24px;
  font-weight: 500;
  color: #303133;
  margin: 0 0 8px 0;
}

.space-id {
  font-size: 14px;
  color: #909399;
  margin: 0 0 12px 0;
}

.api-key-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.api-key-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.api-key-input {
  max-width: 400px;
}

.api-key-input >>> .el-input__inner {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.service-card {
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
  padding: 20px;
}

.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.service-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.service-icon i {
  font-size: 32px;
  color: white;
}

.service-card h3 {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
  margin: 0 0 8px 0;
}

.service-card p {
  font-size: 14px;
  color: #606266;
  margin: 0 0 12px 0;
  line-height: 1.6;
}

.service-stats {
  padding-top: 12px;
  border-top: 1px solid #EBEEF5;
  color: #909399;
  font-size: 14px;
}
</style>
